/**
 * Opens a URL in a new centered window, accounting for screen position and zoom.
 * Defaults to window size with a 100px padding if width or height is not specified.
 *
 * @param {Object}   options            - Configuration options.
 * @param {String}   options.url        - The URL to open.
 * @param {String}   options.title      - The window title.
 * @param {Number}   [options.w]        - Desired window width (in pixels). Defaults to viewport width minus 200px.
 * @param {Number}   [options.h]        - Desired window height (in pixels). Defaults to viewport height minus 200px.
 * @param {Function} [options.onClose]  - Optional callback invoked when the window is closed.
 * @return {WindowProxy|null}           - The new window instance, or null if the window could not be opened.
 */
const openWindow = ( { url, title, w, h, onClose } ) => {
	// Get screen position for dual screens (left/top) with fallback for cross-browser compatibility.
	const dualScreenLeft = window.screenLeft ?? window.screenX;
	const dualScreenTop = window.screenTop ?? window.screenY;

	// Determine viewport width and height with fallbacks.
	const viewportWidth = window.innerWidth || document.documentElement.clientWidth || screen.width;
	const viewportHeight = window.innerHeight || document.documentElement.clientHeight || screen.height;

	// Apply padding of 100px on each side if width or height are not provided.
	const defaultWidth = viewportWidth - 200;
	const defaultHeight = viewportHeight - 200;
	const windowWidth = w ?? defaultWidth;
	const windowHeight = h ?? defaultHeight;

	// Calculate system zoom factor.
	const systemZoom = viewportWidth / window.screen.availWidth;

	/**
	 * Helper to calculate centered positions based on dimensions and zoom.
	 * @param {Number} size          - The size of the window.
	 * @param {Number} viewportSize  - The size of the viewport.
	 * @param {Number} offset        - The offset for the window.
	 * @return {Number}              - The calculated position.
	 */
	const calculatePosition = ( size, viewportSize, offset ) => ( ( viewportSize - size ) / 2 / systemZoom ) + offset;

	// Centered position for the new window.
	const left = calculatePosition( windowWidth, viewportWidth, dualScreenLeft );
	const top = calculatePosition( windowHeight, viewportHeight, dualScreenTop );

	// Open the new window with computed dimensions and position.
	const newWindow = window.open(
		url,
		title,
		`scrollbars=yes,width=${ windowWidth / systemZoom },height=${ windowHeight / systemZoom },top=${ top },left=${ left }`
	);

	// Focus the new window if possible.
	if ( newWindow && newWindow.focus ) {
		newWindow.focus();
	}

	// If an onClose callback is provided, periodically check if the window is closed.
	if ( onClose ) {
		/**
		 * Recursively checks if the window is closed and invokes the onClose callback.
		 */
		const checkWindowClosed = () => {
			if ( newWindow.closed ) {
				onClose();
			} else {
				// Use requestAnimationFrame for smoother polling and reduced CPU load.
				requestAnimationFrame( checkWindowClosed );
			}
		};
		checkWindowClosed();
	}

	return newWindow;
};

export default openWindow;